import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DateRangePicker, Range } from 'react-date-range';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import useOnClickOutside from 'shared/hooks/useOutSideClick';
import { CloseIcon } from 'shared/icon';
import { formatDate } from 'shared/util/utility';

interface IProps {
	formateType?: string;
	title: string;
	onChange: (range: Range) => void;
	isFormSubmit?: boolean;
	required?: boolean;
	errorMsg?: string;
	dateRange?: Range;
	isLabel?: boolean;
	onCancel: () => void;
	className?: string;
	disabled?: boolean;
}

export const DateRange: React.FC<IProps> = ({
	formateType = 'YYYY-MM-DD',
	title,
	onChange,
	isFormSubmit,
	errorMsg,
	required,
	onCancel,
	isLabel = true,
	disabled = false, // Default to false
	dateRange,
	className
}) => {
	const today = useMemo(() => new Date(), []);
	const [ranges, setRanges] = useState<Range>({
		startDate: today,
		endDate: today,
		key: 'selection'
	});
	const [hasSelected, setHasSelected] = useState(false);

	const [isOpen, setIsOpen] = useState(false);

	const popupRef = useRef(null);

	useOnClickOutside(popupRef, () => {
		if (!disabled) setIsOpen(false);
	});
	useEffect(() => {
		if (isFormSubmit) {
			setRanges({
				startDate: today,
				endDate: today,
				key: 'selection'
			});
			setHasSelected(false);
		}
	}, [isFormSubmit, today]);

	useEffect(() => {
		if (dateRange && dateRange.startDate && dateRange.endDate) {
			setRanges({
				...dateRange,
				key: 'selection',
				startDate: new Date(dateRange.startDate),
				endDate: new Date(dateRange.endDate)
			});
			setHasSelected(true);
		}
	}, [dateRange]);

	// Only show as selected if user has picked a range
	const isSelected = hasSelected;
	return (
		<div className={`relative ${disabled ? 'text-themeGray-text' : ''}`} ref={popupRef}>
			{isLabel && (
				<label className={`input-label ${disabled ? 'text-themeGray-text' : ''}`}>
					{title}
					{required && <span className='text-xs'>*</span>}
				</label>
			)}

			<div
				className={`${
					disabled ? '' : 'cursor-pointer'
				} relative text-sm-responsive py-2 px-[6px] border rounded-lg border-themeGray ${
					!isSelected ? 'text-themeGray-text font-semibold' : ''
				}`}
				onClick={() => !disabled && setIsOpen(!isOpen)}
			>
				{isSelected ? (
					<>
						{ranges.startDate ? formatDate(ranges.startDate as unknown as string, formateType) : ''}
						&nbsp; to &nbsp;
						{ranges.endDate ? formatDate(ranges.endDate as unknown as string, formateType) : ''}
						{!disabled && (
							<button
								type='button'
								className='absolute right-2 top-3 text-themeGray-text'
								onClick={(e) => {
									e.stopPropagation();
									setRanges({
										startDate: today,
										endDate: today,
										key: 'selection'
									});
									setHasSelected(false);
									onCancel();
								}}
							>
								<CloseIcon />
							</button>
						)}
					</>
				) : (
					'Select Date'
				)}
			</div>
			{errorMsg && <p className='text-left text-xs text-red-600 first-letter:uppercase'>{errorMsg}</p>}
			{isOpen && !disabled && (
				<div
					className={`date-range-picker absolute z-20 bg-white mt-1 p-1 pb-2 rounded-lg border-themeGray border ${className}`}
				>
					<DateRangePicker
						ranges={[
							{
								...ranges,
								key: 'selection',
								startDate: ranges.startDate ? new Date(ranges.startDate) : today,
								endDate: ranges.endDate ? new Date(ranges.endDate) : today
							}
						]}
						startDatePlaceholder='Start Date'
						endDatePlaceholder='End Date'
						onChange={(item: any) => {
							const { selection } = item;
							setRanges({ ...selection, key: 'selection' }); // always set key
							setHasSelected(true);
							onChange(selection);
						}}
						months={1}
						inputRanges={[]}
						retainEndDateOnFirstSelection={true}
					/>
				</div>
			)}
		</div>
	);
};
